/**
 * @file Core string vocabulary — tagged-template builders (dedent,
 *       fold-to-one-line, escape-by-default HTML, editor-highlightable
 *       SQL) plus lossless case conversion between the repo's naming
 *       conventions.
 * @module @labcraft/core/strings
 *
 * Multiline string literals in source are indented to match the
 * surrounding code, but that indentation leaks into the runtime value
 * unless it is stripped. Across the monorepo this was handled ad-hoc:
 * `[...].join('\n')`, hand-rolled `.replace(/^\s+/gm, …)`, or `+`
 * concatenation to dodge the issue entirely. `@labcraft/email`'s
 * `Template` class solved it correctly but kept the solution private.
 *
 * `CoreString` lifts that solution into core as a shared, dependency-free
 * vocabulary of tagged-template tags. Every tag is a `static` method so
 * it lives on a class per `.agents/rules/typescript.md` § "Functions
 * belong on classes" — a tagged-template call invokes whatever the tag
 * expression resolves to, so `` CoreString.html`…` `` works exactly like a free
 * tag while staying a discoverable member of the `CoreString` namespace.
 *
 * The tags split into two families:
 *
 *   - **Behavioural** — `dedent` (strip common indent, insert values
 *     verbatim), `oneLine` (fold a wrapped literal back to a single
 *     space-joined line — the inverse of `dedent`, for long prose like a
 *     settings `description`), and `html` (dedent + escape every
 *     interpolated value unless wrapped in `raw()`, so a forgotten escape
 *     on user data is impossible). These change the runtime string.
 *
 *   - **Editor-only** — `sql` is `dedent` under a domain-specific name.
 *     It changes nothing the runtime observes (a database normalises
 *     query whitespace); its value is unlocking editor syntax-
 *     highlighting on the embedded SQL via a VS Code template-literal
 *     extension, plus a consistent authoring shape.
 *
 *   - **Case conversion** — `toCamel` / `toPascal` / `toKebab` / `toSnake`
 *     / `toScreamingSnake` move an identifier between the repo's naming
 *     conventions (`.agents/rules/naming.md`: `LC_*` env / `lc_*` snake /
 *     `lc-*` kebab). All five parse through the one canonical model
 *     `splitWords` produces — an ordered list of lowercase words — and
 *     render from it, so any pair round-trips losslessly. The single
 *     precondition is strict camelCase (one capital per word: `apiUrl`,
 *     `diskMib`, never `apiURL`/`diskMiB`); under it `splitWords` is an
 *     exact inverse of every renderer, so no acronym special-casing is
 *     needed. `toEnvKey` and `CoreSettings.wireKey` both delegate here so
 *     an env key and a settings key can never disagree.
 *
 * Lives in `core/shared` (no `node:*`, no runtime dependency) so it is
 * usable from backend, frontend, Service Worker, and test code
 * unchanged. Anything that would need a dependency — a terminal
 * colorizer, a syntax highlighter that renders highlighted code — does
 * NOT belong here; it would violate the `core/shared` dependency
 * direction and lives in a Node-only surface instead.
 */

/**
 * Marks a string as already-safe HTML so `CoreString.html` inserts it verbatim
 * instead of escaping it. Produced only by `CoreString.raw`. Carrying a nominal
 * marker (rather than a bare string) is what makes escape-by-default
 * sound: the only way a value skips escaping is by being explicitly
 * wrapped here.
 *
 * @property value - The trusted HTML fragment, inserted unescaped.
 */
export class RawHtml {
  readonly value: string

  /**
   * @param value - Already-safe HTML to pass through `CoreString.html` unescaped.
   */
  constructor(value: string) {
    this.value = value
  }
}

/**
 * Tagged-template string builders. All members are `static`; invoke the
 * tags as `` CoreString.dedent`…` `` / `` CoreString.oneLine`…` `` / `` CoreString.sql`…` `` /
 * `` CoreString.html`…` ``.
 */
export class CoreString {
  /**
   * Dedent a tagged-template skeleton: strip the common leading indent
   * of the literal lines and trim surrounding blank lines. Value slots
   * are NUL-marked (`\x00`) so a multiline interpolated value can't skew
   * the indent calc — the tags re-insert the values into those slots
   * afterward, leaving runtime values untouched by the dedent.
   *
   * @param strings - The tag's literal parts.
   * @returns The dedented skeleton with `\x00` at each value slot.
   */
  private static dedentSkeleton(strings: TemplateStringsArray): string {
    const lines = strings.join('\x00').split('\n')
    const indents = lines
      .filter(l => l.replace(/\x00/g, '').trim() !== '')
      .map(l => l.match(/^[ \t]*/)![0].length)
    const indent = indents.length ? Math.min(...indents) : 0
    return lines
      .map(l => l.slice(indent))
      .join('\n')
      .trim()
  }

  /**
   * Dedent a multiline literal and insert values verbatim. The
   * foundation tag — `sql` and `css` are this under a different name, and
   * `html` is this plus escape-by-default. Replaces the `[…].join('\n')`
   * and hand-rolled-`.replace` idioms.
   *
   * @param strings - Literal parts.
   * @param values - Interpolated values, inserted as-is (`String(v)`).
   * @returns The dedented string.
   */
  static dedent(strings: TemplateStringsArray, ...values: unknown[]): string {
    let i = 0
    return CoreString.dedentSkeleton(strings).replace(/\x00/g, () =>
      String(values[i++]),
    )
  }

  /**
   * Fold a multiline literal into ONE line: collapse every run of
   * whitespace (the newlines + indentation between words) to a single
   * space, then trim. Lets a long single-line value — a settings / route
   * `description`, a wrapped log message — be authored across indented
   * source lines while the runtime string stays one clean space-joined
   * line. The inverse of `dedent`, which keeps line breaks as `\n`.
   * Interpolated values are inserted verbatim (their own whitespace is
   * NOT folded), so a value carrying newlines survives intact.
   *
   * @param strings - Literal parts.
   * @param values - Interpolated values, inserted as-is.
   * @returns The whitespace-folded single-line string.
   */
  static oneLine(strings: TemplateStringsArray, ...values: unknown[]): string {
    let i = 0
    return strings
      .join('\x00')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\x00/g, () => String(values[i++]))
  }

  /**
   * SQL tag. Behaviourally identical to `dedent` — the database
   * normalises query whitespace, so this changes nothing the runtime
   * observes. Its purpose is to mark the literal as SQL so a VS Code
   * template-literal extension syntax-highlights it, and to give every
   * embedded query one consistent shape. Values are inserted verbatim;
   * this performs NO escaping — use parameterised placeholders (`$1`) for
   * untrusted data, never interpolation.
   *
   * @param strings - Literal parts.
   * @param values - Interpolated SQL fragments, inserted as-is.
   * @returns The dedented query.
   */
  static sql(strings: TemplateStringsArray, ...values: unknown[]): string {
    return CoreString.dedent(strings, ...values)
  }

  /**
   * HTML tag — dedents the literal and **auto-escapes** every
   * interpolated value via `escapeHtml`, except those wrapped in `raw()`
   * (trusted fragments / brand tokens), which pass through. Escape-by-
   * default: a forgotten escape on user-supplied data is impossible. The
   * numeric apostrophe entity (`&#39;`) keeps the output valid as both
   * HTML and XML, so the same tag serves markup and feed builders.
   *
   * @param strings - Literal parts.
   * @param values - Interpolated values (escaped unless `raw()`).
   * @returns The dedented, escaped HTML fragment.
   */
  static html(strings: TemplateStringsArray, ...values: unknown[]): string {
    let i = 0
    return CoreString.dedentSkeleton(strings).replace(/\x00/g, () => {
      const v = values[i++]
      return v instanceof RawHtml ? v.value : CoreString.escapeHtml(String(v))
    })
  }

  /**
   * HTML-escape the five significant characters (`& < > " '`). Called
   * automatically by `html` on every non-`raw` value; exposed for the
   * rare site that escapes a substring outside a tag. The apostrophe maps
   * to the numeric `&#39;` (not `&apos;`) so the result is valid in HTML4
   * as well as XML.
   *
   * @param s - Raw, possibly user-controlled string.
   * @returns The escaped string.
   */
  static escapeHtml(s: string): string {
    return s
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
  }

  /**
   * Wrap a trusted, pre-built HTML fragment (a nested render, a brand
   * token) so `html` inserts it verbatim rather than escaping it.
   *
   * @param html - Already-safe HTML.
   * @returns A marker `html` passes through unescaped.
   */
  static raw(html: string): RawHtml {
    return new RawHtml(html)
  }

  /**
   * Parse any convention into the canonical word list — the pivot every
   * case renderer reads from. Splits on delimiters (`.`, `_`, `-`,
   * whitespace) and on camelCase boundaries (a lower/digit followed by an
   * uppercase), then lowercases each word and drops empties.
   *
   * Under strict camelCase (one capital per word) this is the exact
   * inverse of every renderer below, which is what makes the conversions
   * lossless: `splitWords(render_X(words))` returns `words` for every
   * convention `X`. Non-strict input with an acronym run (`apiURL`,
   * `diskMiB`) still parses deterministically but is not guaranteed to
   * round-trip — the naming discipline keeps identifiers strict so it
   * never arises (see the `@file` header).
   *
   * @param s - An identifier in any supported convention (or a dotted
   *   schema path — dots are treated as word delimiters).
   * @returns The lowercase word list.
   */
  private static splitWords(s: string): string[] {
    return s
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .split(/[\s._-]+/)
      .filter(w => w.length > 0)
      .map(w => w.toLowerCase())
  }

  /**
   * Capitalise the first character of a lowercase word (`disk` → `Disk`).
   * Helper for the camel/Pascal renderers.
   *
   * @param w - A lowercase word from {@link splitWords}.
   * @returns The word with its first character upper-cased.
   */
  private static cap(w: string): string {
    return w.length === 0 ? w : w[0]!.toUpperCase() + w.slice(1)
  }

  /**
   * Render an identifier as strict `camelCase` (`disk-mib` → `diskMib`).
   * The first word stays lowercase; each subsequent word is capitalised.
   *
   * @param s - Identifier in any supported convention.
   * @returns The camelCase form.
   */
  static toCamel(s: string): string {
    return CoreString.splitWords(s)
      .map((w, i) => (i === 0 ? w : CoreString.cap(w)))
      .join('')
  }

  /**
   * Render an identifier as `PascalCase` (`disk-mib` → `DiskMib`).
   *
   * @param s - Identifier in any supported convention.
   * @returns The PascalCase form.
   */
  static toPascal(s: string): string {
    return CoreString.splitWords(s).map(CoreString.cap).join('')
  }

  /**
   * Render an identifier as `kebab-case` (`diskMib` → `disk-mib`) — the
   * `lc-*` wire-identifier convention (HTTP headers, DOM ids, CSS classes,
   * systemd unit names).
   *
   * @param s - Identifier in any supported convention.
   * @returns The kebab-case form.
   */
  static toKebab(s: string): string {
    return CoreString.splitWords(s).join('-')
  }

  /**
   * Render an identifier as `snake_case` (`diskMib` → `disk_mib`) — the
   * `lc_*` convention (cookies, wire/storage keys such as a
   * {@link CoreSettings} field).
   *
   * @param s - Identifier in any supported convention.
   * @returns The snake_case form.
   */
  static toSnake(s: string): string {
    return CoreString.splitWords(s).join('_')
  }

  /**
   * Render an identifier as `SCREAMING_SNAKE_CASE` (`diskMib` →
   * `DISK_MIB`) — the `LC_*` env-var convention. Backs
   * {@link toEnvKey}, which prepends the daemon prefix.
   *
   * @param s - Identifier in any supported convention.
   * @returns The SCREAMING_SNAKE_CASE form.
   */
  static toScreamingSnake(s: string): string {
    return CoreString.toSnake(s).toUpperCase()
  }
}
